// The anonymous gallery: where a visitor parks the images they want to come back
// to. Backed by localStorage, no account and no backend (Phase 2 D7).
//
// An entry is COORDINATES, NOT AN IMAGE. Because renderRegion is deterministic
// from bounds, ~100 bytes of numbers is a lossless record of the artwork — so a
// gallery of hundreds costs nothing to store and nothing to serve. Thumbnails are
// a disposable cache kept elsewhere (see thumbs.js); this file holds the canonical
// record, and nothing in it may become unreconstructable.
//
// The `version` field exists from the very first release on purpose: this payload
// is what Block I migrates into D1 on a user's first authenticated load, and by
// then it will be sitting in real strangers' browsers. A schema change without a
// version to branch on is archaeology.

import coordId from './coordId';

export const STORAGE_KEY = 'fractalart.gallery.v1';
export const SCHEMA_VERSION = 1;

// Fired on every mutation so open views re-read. The `storage` event only fires in
// OTHER tabs, so same-tab updates need this; subscribe() listens to both.
const CHANGE_EVENT = 'fractalart:gallery-changed';

const emptyGallery = () => ({ version: SCHEMA_VERSION, entries: [] });

// Guards against a hand-edited or half-written localStorage value: an entry whose
// bounds are not four finite, correctly-ordered numbers cannot be rendered, so it
// is dropped rather than allowed to break the gallery view.
const isRenderableEntry = (entry) => {
    if (!entry || typeof entry !== 'object') return false;
    const { realMin, realMax, imaginaryMin, imaginaryMax } = entry;
    const bounds = [realMin, realMax, imaginaryMin, imaginaryMax];
    if (!bounds.every(v => typeof v === 'number' && Number.isFinite(v))) return false;
    return realMax > realMin && imaginaryMax > imaginaryMin;
};

// Normalise on read, not on write, so an entry that predates a field (or arrives
// from a future Block I sync) still loads.
//
// `id` is always RECOMPUTED rather than trusted. It is stored for use as the
// /g/:id lookup key and the thumbnail-cache key, but it is derived data, so a
// stored id that disagreed with the bounds would silently split the cache and the
// URL from the actual image. Deriving it on every read makes that impossible.
const normaliseEntry = (entry) => ({
    id: coordId(entry),
    realMin: entry.realMin,
    realMax: entry.realMax,
    imaginaryMin: entry.imaginaryMin,
    imaginaryMax: entry.imaginaryMax,
    savedAt: typeof entry.savedAt === 'string' ? entry.savedAt : new Date(0).toISOString(),
    title: typeof entry.title === 'string' ? entry.title : null
});

// Never throws. localStorage can be absent (SSR), blocked (Safari private mode),
// or hold garbage — in every case an empty gallery is the right answer, because a
// broken read must not take the whole page down with it.
export const loadGallery = () => {
    let raw;
    try {
        raw = window.localStorage.getItem(STORAGE_KEY);
    } catch {
        return emptyGallery();
    }
    if (!raw) return emptyGallery();

    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return emptyGallery();
    }
    if (!parsed || !Array.isArray(parsed.entries)) return emptyGallery();

    return {
        version: Number.isInteger(parsed.version) ? parsed.version : SCHEMA_VERSION,
        entries: parsed.entries.filter(isRenderableEntry).map(normaliseEntry)
    };
};

// Returns true if the write landed. Callers surface the failure rather than
// silently pretending a save happened — an entry the user believes is kept but
// isn't is worse than an error message.
const persist = (gallery) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(gallery));
    } catch {
        return false;
    }
    try {
        window.dispatchEvent(new Event(CHANGE_EVENT));
    } catch {
        /* no window (tests/SSR): the write still counts */
    }
    return true;
};

export const listEntries = () => loadGallery().entries;

// Newest first — the way a person expects their own collection to be ordered.
export const listEntriesNewestFirst = () =>
    [...listEntries()].sort((a, b) => (a.savedAt < b.savedAt ? 1 : a.savedAt > b.savedAt ? -1 : 0));

export const hasEntry = (id) => listEntries().some(e => e.id === id);

export const galleryCount = () => listEntries().length;

// Saving the same region twice is a no-op, not a duplicate: coordId means "this
// image", so two saves of one region are the same specimen. Reports success so
// re-saving still reads as saved in the UI.
export const addEntry = (bounds, { title = null, savedAt } = {}) => {
    if (!isRenderableEntry(bounds)) return { ok: false, id: null };

    const entry = normaliseEntry({
        ...bounds,
        id: coordId(bounds),
        savedAt: savedAt || new Date().toISOString(),
        title
    });

    const gallery = loadGallery();
    if (gallery.entries.some(e => e.id === entry.id)) {
        return { ok: true, id: entry.id, alreadySaved: true };
    }

    gallery.entries.push(entry);
    return { ok: persist(gallery), id: entry.id };
};

export const removeEntry = (id) => {
    const gallery = loadGallery();
    const remaining = gallery.entries.filter(e => e.id !== id);
    if (remaining.length === gallery.entries.length) return true;  // nothing to do
    gallery.entries = remaining;
    return persist(gallery);
};

// Subscribe to gallery mutations from this tab (CHANGE_EVENT) and from other tabs
// (the native `storage` event), so two open windows agree on what is saved.
export const subscribe = (listener) => {
    const onStorage = (event) => {
        if (!event.key || event.key === STORAGE_KEY) listener();
    };
    window.addEventListener(CHANGE_EVENT, listener);
    window.addEventListener('storage', onStorage);
    return () => {
        window.removeEventListener(CHANGE_EVENT, listener);
        window.removeEventListener('storage', onStorage);
    };
};
